import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  makeStyles
} from '@material-ui/core';
import {
  GetTransactionsViaTransactionNoResponse,
  LooseLeafBusinessInfo
} from 'src/types';
import PrintIcon from '@material-ui/icons/Print';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Alert } from '@material-ui/lab';
import { SwitchLabel } from 'src/components';
import { useSnackBar } from 'src/hooks';
import ReceiptIcon from '@material-ui/icons/Receipt';
import WarningIcon from '@material-ui/icons/Warning';
import { colors } from 'src/constants';

interface Props {
  className?: string;
  isSIPricing?: boolean;
  printOpen?: boolean;
  transactionDetails: GetTransactionsViaTransactionNoResponse | null;
  isBlankPage: boolean;
  togglePrintOpen: () => void;
  onClaimStubPrint: () => void;
  toggleLooseLeafPrintOpen: () => void;
  toggleLooseLeaf2307PrintOpen: () => void;
  toggleLooseLeafVATExemptedPrintOpen: () => void;
  togglePrintEUOpen: () => void;
  toggleBlankPage: () => void;
  onUpdateOfTransaction: (
    newData: GetTransactionsViaTransactionNoResponse
  ) => void;
  onSalesInvoicePriceToggle: (v: boolean) => void;
  looseLeafBusinessInfo: LooseLeafBusinessInfo;
  setLooseLeafBusinessInfo: (val: LooseLeafBusinessInfo) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  avatar: {
    height: 100,
    width: 100
  },
  alert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  switchContainer: {
    margin: theme.spacing(0)
  }
}));

export const RenderDetailEditor = ({
  className,
  isSIPricing,
  transactionDetails,
  isBlankPage,
  toggleBlankPage,
  togglePrintOpen,
  togglePrintEUOpen,
  onClaimStubPrint,
  toggleLooseLeafPrintOpen,
  toggleLooseLeaf2307PrintOpen,
  toggleLooseLeafVATExemptedPrintOpen,
  onUpdateOfTransaction,
  onSalesInvoicePriceToggle,
  looseLeafBusinessInfo,
  setLooseLeafBusinessInfo,
  ...rest
}: Props) => {
  const classes = useStyles();
  const snackbar = useSnackBar();

  const [customerName, setCustomerName] = useState<string>(
    () =>
      `${transactionDetails?.customer?.first_name ?? ''} ${transactionDetails
        ?.customer?.last_name ?? ''} `
  );
  const [businessName, setBusinessName] = useState<string>('');
  const [businessAddress, setBusinessAddress] = useState<string>(
    transactionDetails?.business_address ?? ''
  );
  const [tin, setTin] = useState<string>('');
  const [terms, setTerms] = useState<string>('');

  const [datePurchased, setDatePurchased] = useState<
    MaterialUiPickersDate | string
  >(() => {
    if (transactionDetails?.items && transactionDetails?.items?.length > 0) {
      if (transactionDetails?.items[0]?.created_at) {
        return transactionDetails?.items[0].created_at;
      }
    }
    return new Date().toString();
  });

  const [
    tempTransactionDetails,
    setTempTransactionDetails
  ] = useState<GetTransactionsViaTransactionNoResponse | null>(
    transactionDetails
  );

  const onSaveDetailValidate = async () => {
    const missingFields: string[] = [];

    if (!businessName) missingFields.push('Business Name');
    if (!businessAddress) missingFields.push('Business Address');
    if (!tin) missingFields.push('TIN');
    if (!terms) missingFields.push('Terms');

    if (missingFields.length > 0) {
      snackbar.show({
        severity: 'error',
        message: `Please fill in the following fields: ${missingFields.join(
          ', '
        )}`
      });
      return;
    }
    // all should be spread except date and customer name
    let newData: GetTransactionsViaTransactionNoResponse = {
      ...transactionDetails,
      business_name: businessName,
      business_address: businessAddress,
      tin: tin,
      terms: terms,
      // clear Mname and Lname to remove redundancy
      customer: {
        ...(transactionDetails?.customer ?? {}),
        first_name: customerName,
        middle_name: '',
        last_name: '',
        contact_no: tempTransactionDetails?.customer?.contact_no
      }
    };
    newData?.items?.forEach((x) => {
      x.created_at = datePurchased?.toString();
    });
    newData.payment_mode = tempTransactionDetails?.payment_mode;

    onUpdateOfTransaction(newData);
  };

  const onChangeContactNo = (contactNo: string) => {
    setTempTransactionDetails((prev) => ({
      ...prev,
      customer: {
        ...prev?.customer,
        contact_no: contactNo
      }
    }));
  };

  const onChangePaymentMode = (paymentMode: string) => {
    setTempTransactionDetails((prev) => ({
      ...prev,
      payment_mode: paymentMode
    }));
  };

  // const onChangeLooseLeafBusinessInfoField = (info: LooseLeafBusinessInfo) => {
  //   setLooseLeafBusinessInfo(info);
  // };

  useEffect(() => {
    const firstName = transactionDetails?.customer?.first_name?.trim();
    const lastName = transactionDetails?.customer?.last_name?.trim();

    const customerName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || lastName || '';

    setCustomerName(customerName);
    setTempTransactionDetails(transactionDetails);
  }, [transactionDetails]);

  useEffect(() => {
    // TODO: we will improve this, it will much be better if all of this state was centralized into one state
    setBusinessName(transactionDetails?.business_name ?? '');
    setBusinessAddress(transactionDetails?.business_address ?? '');
    setTin(transactionDetails?.tin ?? '');
    setTerms(transactionDetails?.terms ?? '');
  }, [transactionDetails]);

  useEffect(() => {
    if (transactionDetails) {
      setLooseLeafBusinessInfo({
        business_name: transactionDetails?.business_name ?? '',
        business_address: transactionDetails?.business_address ?? '',
        tin: transactionDetails?.tin ?? '',
        terms: transactionDetails?.terms ?? ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionDetails]);

  return (
    <Box m={2}>
      <Alert className={classes.alert} severity="info">
        Editing Information here would only edit the information that is about
        to be printed.
      </Alert>

      <Card className={clsx(classes.root, className)} {...rest}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Customer Name"
                name="name"
                required
                variant="outlined"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <DatePicker
                fullWidth
                size="small"
                format="MMMM dd, yyyy"
                inputVariant="outlined"
                name="purchase_date"
                label="Date Purchased"
                value={datePurchased}
                onChange={(date) => setDatePurchased(date)}
                animateYearScrolling
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                size="small"
                fullWidth
                inputProps={{ maxLength: 11 }}
                helperText="Maximum of 11 characters only. e.g. 09174308787."
                type="tel"
                label="Contact No."
                name="contact_no"
                variant="outlined"
                value={tempTransactionDetails?.customer?.contact_no ?? ''}
                onChange={(e) => onChangeContactNo(e.target.value)}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                size="small"
                helperText="Maximum of 40 characters, ALL CAPS, Add spaces. eg: GCASH - 11,000 DP/COD"
                inputProps={{
                  maxLength: 40,
                  style: { textTransform: 'uppercase' }
                }}
                fullWidth
                label="Payment Mode"
                name="payment_mode"
                variant="outlined"
                value={tempTransactionDetails?.payment_mode ?? ''}
                onChange={(e) => onChangePaymentMode(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <SwitchLabel
                disabled
                checked={isSIPricing}
                label="Toggle SI Pricing"
                className={classes.switchContainer}
                onChange={onSalesInvoicePriceToggle}
              />
            </Grid>

            {transactionDetails?.isSRPByDefault ? (
              <Grid item xs={12}>
                <Alert className={classes.alert} severity="error">
                  THERE IS A HIGH CHANCE THAT THIS WARRANTY SLIP IS ALREADY SRP
                </Alert>
              </Grid>
            ) : null}
          </Grid>
          <Divider style={{ marginTop: 20, marginBottom: 20 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Business Name"
                name="business_name"
                variant="outlined"
                value={
                  looseLeafBusinessInfo?.business_name ?? businessName ?? ''
                }
                onChange={(e) => {
                  setLooseLeafBusinessInfo({
                    ...looseLeafBusinessInfo,
                    business_name: e?.target?.value
                  });
                  setBusinessName(e.target.value);
                }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                size="small"
                fullWidth
                label="TIN"
                name="tin"
                variant="outlined"
                value={looseLeafBusinessInfo?.tin ?? tin ?? ''}
                onChange={(e) => {
                  setLooseLeafBusinessInfo({
                    ...looseLeafBusinessInfo,
                    tin: e?.target?.value
                  });
                  setTin(e.target.value);
                }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Terms"
                name="terms"
                variant="outlined"
                value={looseLeafBusinessInfo?.terms ?? terms ?? ''}
                onChange={(e) => {
                  setLooseLeafBusinessInfo({
                    ...looseLeafBusinessInfo,
                    terms: e?.target?.value
                  });
                  setTerms(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Business Address"
                name="business_address"
                variant="outlined"
                value={
                  businessAddress ??
                  looseLeafBusinessInfo?.business_address ??
                  ''
                }
                onChange={(e) => {
                  setLooseLeafBusinessInfo({
                    ...looseLeafBusinessInfo,
                    business_address: e?.target?.value
                  });
                  setBusinessAddress(e.target.value);
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: '0.5em' }}>
            <Grid item xs={12} md={2}>
              <Button
                onClick={togglePrintOpen}
                color="primary"
                variant="outlined"
                startIcon={<PrintIcon />}
                fullWidth={true}
              >
                ADMIN PRINT
              </Button>
            </Grid>

            <Grid item md={10}>
              <Grid
                container
                alignItems="center"
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item xs={12} sm="auto">
                  <SwitchLabel
                    checked={isBlankPage}
                    label="Show Blank Page"
                    onChange={toggleBlankPage}
                  />
                </Grid>

                <Grid item xs={12} sm="auto">
                  <Button
                    onClick={() => onSaveDetailValidate()}
                    color="primary"
                    variant="contained"
                    fullWidth={true}
                  >
                    Update Information
                  </Button>
                </Grid>

                <Grid item xs={12} sm="auto">
                  <Button
                    onClick={togglePrintEUOpen}
                    color="primary"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    fullWidth={true}
                  >
                    EU WARRANTY CARD PRINT
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Divider style={{ marginTop: 20, marginBottom: 30 }} />

          <Grid
            container
            alignItems="center"
            spacing={2}
            style={{ backgroundColor: colors.lightBlue[50] }}
          >
            <Grid item xs={12} sm={10}>
              <Grid
                container
                alignItems="center"
                spacing={1}
                justifyContent="flex-start"
              >
                <Grid item xs={12} sm="auto">
                  <Button
                    onClick={toggleLooseLeafPrintOpen}
                    color="secondary"
                    variant="contained"
                    startIcon={<ReceiptIcon />}
                    endIcon={<WarningIcon />}
                    fullWidth
                  >
                    !!! LOOSELEAF PRINT !!!
                  </Button>
                </Grid>

                <Grid item xs={12} sm="auto">
                  <Button
                    onClick={toggleLooseLeaf2307PrintOpen}
                    color="secondary"
                    variant="outlined"
                    startIcon={<ReceiptIcon />}
                    endIcon={<WarningIcon />}
                    fullWidth
                  >
                    !!! 2307 LOOSELEAF PRINT !!!
                  </Button>
                </Grid>

                <Grid item xs={12} sm="auto">
                  <Button
                    onClick={toggleLooseLeafVATExemptedPrintOpen}
                    color="secondary"
                    variant="outlined"
                    startIcon={<ReceiptIcon />}
                    endIcon={<WarningIcon />}
                    fullWidth
                  >
                    !!! VAT EXEMPTED LOOSELEAF PRINT !!!
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                onClick={onClaimStubPrint}
                color="primary"
                variant="outlined"
                startIcon={<PrintIcon />}
                fullWidth
              >
                CLAIM STUB PRINT
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
